import { type ComponentPropsWithoutRef } from 'react';
import { cn } from '@/lib/utils';

type IconProps = ComponentPropsWithoutRef<'svg'>;

const ArrowRightIcon = ({ className, strokeWidth = 1.5, ...props }: IconProps & { strokeWidth?: number | string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        fill="none"
        strokeWidth={strokeWidth}
        aria-hidden="true"
        className={cn('h-4 w-4 shrink-0', className)}
        {...props}>
        <path
            d="M2.5 8H13.5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
        />
        <path
            d="M9 3.5L13.5 8L9 12.5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
        />
    </svg>
);

const PlusIcon = ({ className, ...props }: IconProps) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
        className={cn('h-8 w-8 shrink-0', className)}
        {...props}>
        <path
            d="M5 16H27"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
        />
        <path
            d="M16 5V27"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
        />
    </svg>
);

const MinusIcon = ({ className, ...props }: IconProps) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
        className={cn('h-8 w-8 shrink-0', className)}
        {...props}>
        <path
            d="M5 16H27"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
        />
    </svg>
);

const FacebookIcon = ({ className, ...props }: IconProps) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
        className={cn('h-8 w-8 shrink-0', className)}
        {...props}>
        <circle
            cx="16"
            cy="16"
            r="12"
            stroke="currentColor"
            strokeWidth="1.5"
        />
        <path
            d="M21 11H19C18.2044 11 17.4413 11.3161 16.8787 11.8787C16.3161 12.4413 16 13.2044 16 14V28"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M12 18H20"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const InstagramIcon = ({ className, ...props }: IconProps) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
        className={cn('size-8 shrink-0', className)}
        {...props}
    >
        <path
            d="M16 21C18.7614 21 21 18.7614 21 16C21 13.2386 18.7614 11 16 11C13.2386 11 11 13.2386 11 16C11 18.7614 13.2386 21 16 21Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M22 4H10C6.68629 4 4 6.68629 4 10V22C4 25.3137 6.68629 28 10 28H22C25.3137 28 28 25.3137 28 22V10C28 6.68629 25.3137 4 22 4Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M22.5 10.75C23.1904 10.75 23.75 10.1904 23.75 9.5C23.75 8.80964 23.1904 8.25 22.5 8.25C21.8096 8.25 21.25 8.80964 21.25 9.5C21.25 10.1904 21.8096 10.75 22.5 10.75Z"
            fill="currentColor"
        />
    </svg>
);

const XIcon = ({ className, ...props }: IconProps) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
        className={cn('size-8 shrink-0', className)}
        {...props}
    >
        <path
            d="M22.1125 6.07812H25.6401L17.9351 14.9073L27 26.9219H19.9031L14.3442 19.6338L7.98331 26.9219H4.45425L12.6954 17.4775L4 6.07812H11.2781L16.3021 12.7381L22.1125 6.07812ZM20.8762 24.8059H22.8313L10.2143 8.08344H8.11844L20.8762 24.8059Z"
            fill="currentColor"
        />
    </svg>
);

const PhoneIcon = ({ className, ...props }: IconProps) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
        className={cn('size-4 shrink-0', className)}
        {...props}
    >
        <path
            d="M10.2744 9.08398C10.3436 9.0379 10.4233 9.00983 10.5061 9.0023C10.5889 8.99477 10.6723 9.00802 10.7488 9.04086L13.6963 10.3615C13.7956 10.4039 13.8785 10.4774 13.9326 10.5709C13.9867 10.6644 14.009 10.773 13.9963 10.8802C13.8991 11.6059 13.5418 12.2716 12.9907 12.7535C12.4395 13.2354 11.7321 13.5008 11 13.5002C8.74566 13.5002 6.58365 12.6047 4.98959 11.0106C3.39553 9.41658 2.5 7.25457 2.5 5.00023C2.49944 4.26811 2.7648 3.5607 3.24673 3.00956C3.72865 2.45843 4.39435 2.10108 5.12 2.00398C5.22727 1.99124 5.33578 2.01358 5.4293 2.06766C5.52281 2.12174 5.5963 2.20465 5.63875 2.30398L6.95938 5.25398C6.99182 5.32975 7.00504 5.41236 6.99784 5.49447C6.99064 5.57658 6.96326 5.65564 6.91813 5.72461L5.5825 7.31273C5.53512 7.38422 5.50711 7.46678 5.50119 7.55234C5.49528 7.6379 5.51166 7.72353 5.54875 7.80086C6.06563 8.85898 7.15938 9.93961 8.22063 10.4515C8.29836 10.4884 8.38439 10.5044 8.47021 10.498C8.55602 10.4915 8.63867 10.4628 8.71 10.4146L10.2744 9.08398Z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const UserIcon = ({ className, ...props }: IconProps) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 36 36"
        fill="none"
        aria-hidden="true"
        className={cn('size-9 shrink-0', className)}
        {...props}
    >
        <path
            d="M26.1667 28.5V26.1667C26.1667 24.929 25.675 23.742 24.7999 22.8668C23.9247 21.9917 22.7377 21.5 21.5 21.5H14.5C13.2624 21.5 12.0754 21.9917 11.2002 22.8668C10.325 23.742 9.83337 24.929 9.83337 26.1667V28.5M22.6667 12.1667C22.6667 14.744 20.5774 16.8333 18 16.8333C15.4227 16.8333 13.3334 14.744 13.3334 12.1667C13.3334 9.58934 15.4227 7.5 18 7.5C20.5774 7.5 22.6667 9.58934 22.6667 12.1667Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="[stroke-width:1.5] md:[stroke-width:1]"
        />
    </svg>
);

const CartIcon = ({ className, ...props }: IconProps) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 36 36"
        fill="none"
        aria-hidden="true"
        className={cn('size-9 shrink-0', className)}
        {...props}
    >
        <path
            d="M6.39148 6.39062H8.72481L11.8281 20.8806C11.942 21.4113 12.2373 21.8857 12.6631 22.2221C13.089 22.5586 13.6189 22.736 14.1615 22.724H25.5715C26.1025 22.7231 26.6174 22.5411 27.031 22.2081C27.4446 21.8751 27.7323 21.4109 27.8465 20.8923L29.7715 12.224H9.97315M14.4998 28.4989C14.4998 29.1432 13.9774 29.6656 13.3331 29.6656C12.6888 29.6656 12.1664 29.1432 12.1664 28.4989C12.1664 27.8546 12.6888 27.3322 13.3331 27.3322C13.9774 27.3322 14.4998 27.8546 14.4998 28.4989ZM27.3331 28.4989C27.3331 29.1432 26.8108 29.6656 26.1664 29.6656C25.5221 29.6656 24.9998 29.1432 24.9998 28.4989C24.9998 27.8546 25.5221 27.3322 26.1664 27.3322C26.8108 27.3322 27.3331 27.8546 27.3331 28.4989Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="[stroke-width:1.5] md:[stroke-width:1]"
        />
    </svg>
);

const WishlistHeartIcon = ({ className, ...props }: IconProps) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 36 36"
        fill="none"
        aria-hidden="true"
        className={cn('size-9 shrink-0', className)}
        {...props}
    >
        <path
            d="M26.1667 20.3333C27.905 18.63 29.6667 16.5883 29.6667 13.9167C29.6667 12.2149 28.9907 10.5828 27.7873 9.3794C26.584 8.17604 24.9518 7.5 23.25 7.5C21.1967 7.5 19.75 8.08333 18 9.83333C16.25 8.08333 14.8034 7.5 12.75 7.5C11.0482 7.5 9.41613 8.17604 8.21277 9.3794C7.00941 10.5828 6.33337 12.2149 6.33337 13.9167C6.33337 16.6 8.08337 18.6417 9.83337 20.3333L18 28.5L26.1667 20.3333Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="[stroke-width:1.5] md:[stroke-width:1]"
        />
    </svg>
);


const EditIcon = ({ className, ...props }: IconProps) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className={cn('h-6 w-6 shrink-0', className)}
        {...props}>
        <path
            d="M8.68969 20.2501H4.5C4.30109 20.2501 4.11032 20.1711 3.96967 20.0305C3.82902 19.8898 3.75 19.699 3.75 19.5001V15.3104C3.75009 15.1118 3.82899 14.9213 3.96938 14.7807L15.5306 3.2195C15.6713 3.07895 15.862 3 16.0608 3C16.2596 3 16.4503 3.07895 16.5909 3.2195L20.7806 7.40637C20.9212 7.54701 21.0001 7.7377 21.0001 7.93653C21.0001 8.13535 20.9212 8.32605 20.7806 8.46668L9.21937 20.0307C9.07883 20.1711 8.88834 20.25 8.68969 20.2501Z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
        />
        <path
            d="M12.75 6L18 11.25"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
        />
    </svg>
);

const DeleteIcon = ({ className, ...props }: IconProps) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className={cn('h-6 w-6 shrink-0', className)}
        {...props}>
        <path
            d="M20.25 5.25H3.75"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
        />
        <path
            d="M9.75 9.75V15.75"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
        />
        <path
            d="M14.25 9.75V15.75"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
        />
        <path
            d="M18.75 5.25V19.5C18.75 19.6989 18.671 19.8897 18.5303 20.0303C18.3897 20.171 18.1989 20.25 18 20.25H6C5.80109 20.25 5.61032 20.171 5.46967 20.0303C5.32902 19.8897 5.25 19.6989 5.25 19.5V5.25"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
        />
        <path
            d="M15.75 5.25V3.75C15.75 3.35218 15.592 2.97064 15.3107 2.68934C15.0294 2.40804 14.6478 2.25 14.25 2.25H9.75C9.35218 2.25 8.97064 2.40804 8.68934 2.68934C8.40804 2.97064 8.25 3.35218 8.25 3.75V5.25"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
        />
    </svg>
);

const HeaderSearchIcon = ({
    className,
    viewBox = '8 8 20 20',
    strokeWidth = 0.8,
    ...props
}: IconProps & { viewBox?: string; strokeWidth?: number | string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={viewBox}
        fill="none"
        aria-hidden="true"
        className={cn('size-9 shrink-0', className)}
        {...props}
    >
        <path
            d="M24 24L21.1333 21.1333M22.6667 17.3333C22.6667 20.2789 20.2789 22.6667 17.3333 22.6667C14.3878 22.6667 12 20.2789 12 17.3333C12 14.3878 14.3878 12 17.3333 12C20.2789 12 22.6667 14.3878 22.6667 17.3333Z"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ strokeWidth }}
        />
    </svg>
);

const FiltersIcon = ({
    className,
    strokeWidth = 2,
    ...props
}: IconProps & { strokeWidth?: number | string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
        className={cn('size-5 shrink-0', className)}
        {...props}>
        <path
            d="M13 13C14.6569 13 16 11.6569 16 10C16 8.34315 14.6569 7 13 7C11.3431 7 10 8.34315 10 10C10 11.6569 11.3431 13 13 13Z"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M21 25C22.6569 25 24 23.6569 24 22C24 20.3431 22.6569 19 21 19C19.3431 19 18 20.3431 18 22C18 23.6569 19.3431 25 21 25Z"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M16 10H27"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M5 10H10"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M24 22H27"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M5 22H18"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export { ArrowRightIcon, PlusIcon, FacebookIcon, InstagramIcon, XIcon, PhoneIcon, UserIcon, CartIcon, WishlistHeartIcon, EditIcon, MinusIcon, DeleteIcon, HeaderSearchIcon, FiltersIcon };
